import { atom, map } from 'nanostores';
import type { WebContainer } from '@webcontainer/api';
import { editorStore } from './editor';

/** Set when `executeBuild` stops at npm install, Vite, or `tsc --noEmit`. */
export type WorkbenchBuildFailurePhase = 'npm-install' | 'vite' | 'tsc';

export interface WorkbenchBuildFailure {
  phase: WorkbenchBuildFailurePhase;
  errors: string;
  output: string;
  at: number;
}

/** AI attempted to overwrite a path the user edited manually during streaming. */
export interface WorkbenchFileConflict {
  aiContent: string;
  userContent: string;
  /** Path the AI tool targeted (may differ from store key after ts→tsx promotion). */
  aiPath?: string;
}

export const workbenchStore = {
  webcontainer: atom<WebContainer | null>(null),

  brandLogoSrc: atom<string | undefined>(undefined),

  session: atom<{ mode: string; skills: string[] }>({ mode: 'website', skills: [] }),

  files: map<Record<string, { content: string; modified?: boolean }>>({}),

  /** Binary assets keyed by WebContainer path (e.g. public/media/hash.jpg). */
  assets: map<Record<string, { base64: string; mimeType?: string }>>({}),

  /** Last persisted tool-write content per path (for unified diff vs manual edits). Updated by executeWriteFile. */
  lastToolWrittenByPath: atom<Record<string, string>>({}),

  /** path → conflict when AI writeFile collides with `modified: true` user content. */
  fileConflicts: map<Record<string, WorkbenchFileConflict>>({}),

  /** Set when manual-edit context is capped before a build-stream POST. */
  manualContextTruncationWarning: atom<string | null>(null),

  previewUrl: atom<string>(''),

  /**
   * True after a prototype snapshot zip was applied — allows the preview iframe
   * without requiring a fresh production build (Publish still gates on buildStatus).
   */
  snapshotPreviewReady: atom<boolean>(false),

  /**
   * True once the preview iframe is genuinely showing the generated app — not just once the
   * build-stream itself finished. Set by `Workbench` from the same `bridgeReady` signal that
   * gates its own loading overlay (the injected preview-bridge checking in from inside the
   * iframe, not the iframe's native `load` event — see the comment above `previewWaitingForIframe`
   * there for why that distinction matters). Consumers outside the workbench tree — e.g. the
   * panel toolbar deciding when to reveal "View Insights" / "Publish" — read this instead of a
   * stream-completion flag, so those controls don't appear over what's still a loading screen.
   */
  previewReady: atom<boolean>(false),

  buildStatus: atom<'idle' | 'building' | 'success' | 'error'>('idle'),

  /**
   * True once a manual editor edit has landed in `files`/`assets` since the last successful
   * `executeBuild()`. `buildStatus` alone can't tell a caller this — it stays `'success'` from
   * the previous build until a *new* build starts, so a hand-edit made after that build has
   * nothing to flip it. Consumers (Publish's disabled-reason) use this to warn that the
   * snapshot they'd publish now diverges from what was last actually built and verified.
   * Set `true` by the editor's `onChange`; cleared back to `false` by every path that either
   * runs a fresh successful build (`Workbench`'s Rebuild button, the build-stream tool call) or
   * replaces the whole file set outright (snapshot restore, template reset) — both make the
   * store's content match what was just built/loaded again.
   */
  hasUnbuiltEdits: atom<boolean>(false),

  isStreaming: atom<boolean>(false),

  /** True while a client tool (`writeFile`, `build`, etc.) is executing in WebContainer. */
  clientToolsBusy: atom<boolean>(false),

  depsInstalled: atom<boolean>(false),

  /** WebContainer boot lifecycle for UI (skeleton / errors). */
  bootStatus: atom<'idle' | 'booting' | 'ready' | 'error'>('idle'),
  bootError: atom<string | undefined>(undefined),

  /** Idle prewarm lifecycle (app shell schedules boot early). */
  prewarmState: atom<'idle' | 'scheduled' | 'running' | 'ready' | 'skipped' | 'failed'>('idle'),

  /** Vite dev server health inside WebContainer. */
  devServerStatus: atom<'idle' | 'starting' | 'ready' | 'crashed' | 'error'>('idle'),

  /** Suppress auto-snapshot while restoring a zip into the container. */
  restoreInProgress: atom<boolean>(false),

  /** Last failed production build (cleared when a new build starts). Subscribe or listen for `workbench:build-failed` / `workbench:tsc-failed`. */
  lastBuildFailure: atom<WorkbenchBuildFailure | null>(null),

  getFiles(): [string, string][] {
    const files = workbenchStore.files.get();
    return Object.entries(files).map(([path, f]) => [path, f.content]);
  },

  getModifiedFiles(): Record<string, string> {
    const files = workbenchStore.files.get();
    const modified: Record<string, string> = {};
    for (const [path, f] of Object.entries(files)) {
      if (f.modified) modified[path] = f.content;
    }
    return modified;
  },

  /** After a Chef turn completes with no pending client tools; marks user edits consumed for next build-stream body. */
  clearManualEditConsumedFlags(): void {
    const conflicts = workbenchStore.fileConflicts.get();
    for (const [path, f] of Object.entries(workbenchStore.files.get())) {
      if (f.modified && !conflicts[path]) {
        workbenchStore.files.setKey(path, { content: f.content, modified: false });
      }
    }
  },

  setFileConflict(path: string, conflict: WorkbenchFileConflict): void {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!trimmed) return;
    workbenchStore.fileConflicts.setKey(trimmed, conflict);
  },

  clearFileConflict(path: string): void {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!trimmed) return;
    const next = { ...workbenchStore.fileConflicts.get() };
    delete next[trimmed];
    workbenchStore.fileConflicts.set(next);
  },

  getFileConflictPaths(): string[] {
    return Object.keys(workbenchStore.fileConflicts.get());
  },

  hasFileConflicts(): boolean {
    return Object.keys(workbenchStore.fileConflicts.get()).length > 0;
  },

  recordToolWriteSnapshot(path: string, content: string): void {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!trimmed) return;
    const next = { ...workbenchStore.lastToolWrittenByPath.get(), [trimmed]: content };
    workbenchStore.lastToolWrittenByPath.set(next);
  },

  removeFile(path: string): void {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    if (!trimmed) return;

    const files = { ...workbenchStore.files.get() };
    delete files[trimmed];
    workbenchStore.files.set(files);

    const assets = { ...workbenchStore.assets.get() };
    delete assets[trimmed];
    workbenchStore.assets.set(assets);

    const snapshots = { ...workbenchStore.lastToolWrittenByPath.get() };
    delete snapshots[trimmed];
    workbenchStore.lastToolWrittenByPath.set(snapshots);

    workbenchStore.clearFileConflict(trimmed);

    const selected = editorStore.selectedFile.get();
    if (selected === trimmed) {
      editorStore.selectedFile.set(null);
    }
  },

  /** Baseline body after last Chef/tool write — used only for computing manual-edit diffs. */
  getLastToolWritten(path: string): string | undefined {
    const trimmed = path.replace(/\\/g, '/').replace(/^\/+/, '');
    return workbenchStore.lastToolWrittenByPath.get()[trimmed];
  },

  reset(): void {
    /** Logs stay until the user clears them — resetting other workbench state runs often on effect cleanup. */
    workbenchStore.brandLogoSrc.set(undefined);
    workbenchStore.session.set({ mode: 'website', skills: [] });
    workbenchStore.files.set({});
    workbenchStore.assets.set({});
    workbenchStore.lastToolWrittenByPath.set({});
    workbenchStore.fileConflicts.set({});
    workbenchStore.manualContextTruncationWarning.set(null);
    workbenchStore.previewUrl.set('');
    workbenchStore.snapshotPreviewReady.set(false);
    workbenchStore.previewReady.set(false);
    workbenchStore.buildStatus.set('idle');
    workbenchStore.isStreaming.set(false);
    workbenchStore.clientToolsBusy.set(false);
    workbenchStore.bootStatus.set('idle');
    workbenchStore.bootError.set(undefined);
    workbenchStore.prewarmState.set('idle');
    workbenchStore.devServerStatus.set('idle');
    workbenchStore.restoreInProgress.set(false);
    workbenchStore.lastBuildFailure.set(null);
    editorStore.selectedFile.set(null);
    editorStore.scrollPosition.set({ line: 0, ch: 0 });
  },
};
